import { ArrowLeft20, Close20, Search16 } from '@carbon/icons-react';
import { isNaN } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { ProjectFileGroup } from 'api/project-files/types';
import {
  CpBody03Secondary,
  CpHeading03Primary,
} from 'components/atoms/customer-portal/typography';
import { RoutePaths } from 'constant';
import { useIsTablet } from 'hooks/use-media-query';
import { useTranslate } from 'hooks/use-translation';
import { handleDownload } from 'utils/attachment';
import { CpInput } from '../../components/cp-input';
import { useGetBackground } from '../../types/utils';
import { CpWidgetProps } from '../../types/widget';
import { useCustomerPortalHashMap } from '../../utils';
import { StyledBackWrapper } from '../all-files/cp-all-files';
import { ListOrGridMode } from '../all-files/types';
import { BackIconWrapper } from '../chat/components';
import { CpSectionWrapper } from '../common';
import { CpAccountFilesForLaptop } from './cp-account-files.laptop';
import { CpAccountFilesForMobile } from './cp-account-files.mobile';
import { CpAccountFilesData } from './types';

const Title = styled(CpHeading03Primary)`
  margin-bottom: var(--cp-spacing-03);

  @media (max-width: 480px) {
    margin-bottom: var(--cp-spacing-03);
  }
`;

const Description = styled(CpBody03Secondary)`
  margin-bottom: var(--cp-spacing-05);

  @media (max-width: 480px) {
    flex: 1 1 auto; /* Allow the item to grow and shrink */
    overflow: hidden; /* Hide overflow text */
    white-space: normal; /* Prevent text from wrapping */
    /* ellipsize after two lines */

    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    box-orient: vertical;
    line-clamp: 2;
    overflow: hidden;
  }
`;

const Input = styled(CpInput)`
  margin-bottom: var(--cp-spacing-06);
  border-radius: var(--border-radius-small);
  border: 1px solid var(--cp-border-subtle);
  height: 48px;
  padding: 0 var(--cp-spacing-06);

  .ant-input-suffix {
    height: 32px;
    width: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: auto;
    transition: all 0.3s;
    border-radius: var(--border-radius-base);
    :hover {
      background: var(--cp-gray-20);
      cursor: pointer;
    }
  }

  .ant-input-prefix {
    margin-right: var(--cp-spacing-03);
  }

  &.ant-input-affix-wrapper {
    height: 48px;
  }
  @media (max-width: 480px) {
    margin-bottom: var(--cp-spacing-05);
  }
`;

export const CpAccountFiles: React.FC<CpWidgetProps> = ({
  widget,
  data: _data,
  projectId,
  disabled,
}) => {
  const router = useHistory();
  const isResponsive = useIsTablet();
  const background = useGetBackground(widget);
  const data = useMemo(() => _data as CpAccountFilesData, [_data]);
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState<ListOrGridMode>(ListOrGridMode.Grid);
  const { hashMap, setHashMap, removeKey } = useCustomerPortalHashMap();
  const translations = useTranslate();
  const _currentFolder: ProjectFileGroup | undefined = useMemo(() => {
    const split = hashMap?.acc_folder?.split('.');
    const isCurrentProjectANumber = !isNaN(Number(split?.[0]));
    const isCurrentFolderANumber = !isNaN(Number(split?.[1]));

    if (isCurrentFolderANumber && isCurrentProjectANumber) {
      const folderProjectId = Number(split?.[0]);
      const folderId = Number(split?.[1]);

      const project = data?.folders.find(
        (folder) => folder.project.projectId === folderProjectId,
      );

      const folder = project?.folders?.find((f) => f.folderId === folderId);

      if (folder) {
        return {
          ...folder,
          folderName: search?.trim()
            ? translations('search_results')
            : folder.folderName,
        };
      }

      return folder;
    }

    if (isResponsive) {
      return undefined;
    }

    return data?.folders?.at(0)?.folders?.at(0);
  }, [data?.folders, hashMap?.acc_folder, isResponsive, search, translations]);

  const currentFolder = useMemo(() => {
    if (search?.trim() && _currentFolder) {
      return {
        ..._currentFolder,
        folderName: translations('search_results'),
      };
    }

    return _currentFolder;
  }, [search, _currentFolder, translations]);

  const allAttachments = useMemo(() => {
    return (
      data?.folders
        ?.flatMap((e) => e?.folders)
        ?.flatMap((e) => e?.attachments) ?? []
    );
  }, [data?.folders]);

  const currentAttachments = useMemo(() => {
    if (search?.trim()) {
      return (
        allAttachments?.filter((e) =>
          e?.name?.toLowerCase?.()?.includes(search?.trim?.().toLowerCase?.()),
        ) ?? []
      );
    }

    const attachments = currentFolder?.attachments;

    return (
      attachments?.filter((e) =>
        e?.name?.toLowerCase?.()?.includes(search?.trim?.().toLowerCase?.()),
      ) ?? []
    );
  }, [allAttachments, currentFolder?.attachments, search]);

  const onFolderClick = useCallback(
    (
      args:
        | {
            projectId: number;
            folderId: number;
          }
        | undefined,
    ) => {
      if (!args || !args?.folderId || !args?.projectId) {
        removeKey('acc_folder');

        return;
      }
      setHashMap('acc_folder', `${args.projectId}.${args.folderId}`);
    },
    [removeKey, setHashMap],
  );

  const onFileClick = useCallback(
    (attachmentId: number) => {
      router.push(
        RoutePaths.customerPortalAttachmentById
          .replace(':projectId', projectId.toString())
          .replace(':attachmentId', attachmentId.toString()),
      );
    },
    [projectId, router],
  );

  return (
    <CpSectionWrapper
      background={background}
      paddingY={
        widget?.widgetDefinition?.styles?.paddingY as number | undefined
      }
      isDisabled={disabled}
    >
      {isResponsive && currentFolder ? (
        <>
          <StyledBackWrapper>
            <BackIconWrapper onClick={() => onFolderClick(undefined)}>
              <ArrowLeft20 />
            </BackIconWrapper>
            <Title autoAdjustColor>{currentFolder?.folderName}</Title>
          </StyledBackWrapper>
        </>
      ) : (
        <>
          <Title autoAdjustColor>{data.title}</Title>
          <Description autoAdjustColor>{data.description}</Description>
        </>
      )}
      <Input
        prefix={<Search16 />}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={translations('search')}
        suffix={search ? <Close20 onClick={() => setSearch('')} /> : undefined}
      />
      {isResponsive ? (
        <CpAccountFilesForMobile
          handleDownload={handleDownload}
          currentAttachments={currentAttachments}
          currentFolder={currentFolder}
          data={data}
          mode={mode}
          onFolderClick={onFolderClick}
          search={search}
          setMode={setMode}
          onFileClick={onFileClick}
          projectId={projectId}
        />
      ) : (
        <CpAccountFilesForLaptop
          handleDownload={handleDownload}
          currentAttachments={currentAttachments}
          currentFolder={currentFolder}
          data={data}
          mode={mode}
          onFolderClick={onFolderClick}
          search={search}
          setMode={setMode}
          onFileClick={onFileClick}
          projectId={projectId}
        />
      )}
    </CpSectionWrapper>
  );
};
